import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { addDays, format, formatDistanceToNow, subDays } from "date-fns";
import { Loader2, Plug, RefreshCw } from "lucide-react";
import {
  Alert,
  Button,
  DateRangeInput,
  ErrorPage,
  FeedbackState,
  Field,
  FilterBar,
  PageContent,
  PageHeader,
  PageShell,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Tabs,
} from "@h2j-data-team/ui";
import type { ReloadCollection } from "@repo/shared";

import { toast } from "@/components/shared";
import { supportCode } from "@/components/error-screen";
import { getErrorMessage } from "@/lib/api-client";
import type { ReloadPeriodParams } from "@/lib/api/reloads";
import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";
import {
  reloadAlertsQueryOptions,
  reloadCollectionQueryOptions,
  useSetReloadCollection,
} from "@/features/reloads/api";
import {
  AlertsView,
  AppRunsDrawer,
  BottlenecksView,
  ErrorsView,
  NowView,
  PerformanceView,
  RELOAD_ORIGINS,
  reloadOriginLabel,
} from "@/components/reloads";

const VIEWS = ["now", "errors", "bottlenecks", "performance", "alerts"] as const;
type View = (typeof VIEWS)[number];

type ReloadsSearch = {
  connection?: string;
  view?: View;
  // YYYY-MM-DD, both inclusive; omitted = the last 30 days
  from?: string;
  to?: string;
  origin?: string;
  // the app whose runs drawer is open
  app?: string;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DEFAULT_PERIOD_DAYS = 30;
// Hours and weekdays are bucketed in the viewer's own zone.
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const ALL = "all";

const day = (value: unknown) => (typeof value === "string" && DAY.test(value) ? value : undefined);
// A day's local midnight: the instant the period starts (or, the day after
// `to`, ends) at.
const dayStart = (value: string) => new Date(`${value}T00:00:00`);

export const Route = createFileRoute("/_authenticated/reloads")({
  validateSearch: (search: Record<string, unknown>): ReloadsSearch => ({
    connection: typeof search.connection === "string" ? search.connection : undefined,
    // "apps" became the Performance tab: saved links land there.
    view:
      search.view === "apps"
        ? "performance"
        : VIEWS.includes(search.view as View)
          ? (search.view as View)
          : undefined,
    from: day(search.from),
    to: day(search.to),
    origin: typeof search.origin === "string" ? search.origin : undefined,
    app: typeof search.app === "string" ? search.app : undefined,
  }),
  staticData: { pageTitle: "breadcrumbs.reloads" },
  component: ReloadsPage,
});

function ReloadsPage() {
  const { t, i18n } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const { formatDate } = useFormatDate();
  const { dateFnsLocale } = useLocale();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ReloadsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }), replace: true });

  const view = search.view ?? "now";
  const connectionsQuery = useQuery(connectionsQueryOptions);
  const connections = connectionsQuery.data;
  // An id outside the org's own list (another org's, or a deleted one) falls
  // back to the first connection instead of a dead page.
  const connection = connections?.find((c) => c.id === search.connection) ?? connections?.[0];
  const connectionId = connection?.id ?? "";

  const collectionQuery = useQuery(reloadCollectionQueryOptions(connectionId));
  const collection = collectionQuery.data;
  const setCollection = useSetReloadCollection(connectionId);
  const hasHistory = (collection?.reloadCount ?? 0) > 0;
  // A finished collection bumps it, which refetches the collected views.
  const version = collection?.lastCollectedAt ?? "none";

  const today = format(new Date(), "yyyy-MM-dd");
  const range = {
    from: search.from ?? format(subDays(dayStart(today), DEFAULT_PERIOD_DAYS - 1), "yyyy-MM-dd"),
    to: search.to ?? today,
  };
  const period = useMemo<ReloadPeriodParams>(
    () => ({
      from: dayStart(range.from).toISOString(),
      to: addDays(dayStart(range.to), 1).toISOString(),
      tz: TIME_ZONE,
      type: search.origin,
    }),
    [range.from, range.to, search.origin],
  );

  const alertsQuery = useQuery({
    ...reloadAlertsQueryOptions(connectionId, version),
    enabled: !!connectionId && hasHistory,
  });

  const toggleCollection = (enabled: boolean) =>
    setCollection.mutate(enabled, {
      onSuccess: () =>
        toast.success(t(enabled ? "collection.toast.enabled" : "collection.toast.disabled")),
      onError: (err) => toast.error(getErrorMessage(err, t("collection.toast.failed"))),
    });

  const collectionStatus = (c: ReloadCollection): string => {
    if (!c.enabled) return t("collection.off");
    if (c.lastError) return t("collection.failed");
    if (!c.lastCollectedAt) return t("collection.firstRun");
    return t("collection.collected", {
      count: c.reloadCount,
      formatted: c.reloadCount.toLocaleString(i18n.language),
      when: formatDistanceToNow(new Date(c.lastCollectedAt), {
        addSuffix: true,
        locale: dateFnsLocale,
      }),
      since: c.oldestReloadAt ? formatDate(c.oldestReloadAt) : "—",
    });
  };

  const header = <PageHeader title={t("title")} description={t("description")} />;

  if (connectionsQuery.isPending) {
    return (
      <PageShell header={header}>
        <PageContent>
          <Skeleton className="h-40 w-full" />
        </PageContent>
      </PageShell>
    );
  }

  if (connectionsQuery.isError) {
    return (
      <ErrorPage
        kind="load-failed"
        header={header}
        title={t("error.load")}
        supportCode={supportCode(connectionsQuery.error, tc("error.supportCode"))}
        actions={
          <Button variant="outline" onClick={() => void connectionsQuery.refetch()}>
            <RefreshCw />
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  if (!connection) {
    return (
      <PageShell header={header}>
        <PageContent>
          <FeedbackState
            state="empty"
            icon={<Plug />}
            title={t("empty.noConnections.title")}
            description={t("empty.noConnections.description")}
            action={
              <Button asChild>
                <Link to="/connections/new">{t("empty.noConnections.action")}</Link>
              </Button>
            }
          />
        </PageContent>
      </PageShell>
    );
  }

  const periodView = view === "errors" || view === "bottlenecks" || view === "performance";

  const filters = (
    <FilterBar
      aria-label={t("filters.label")}
      actions={
        collection && (
          <>
            <span className="text-sm text-dim">{collectionStatus(collection)}</span>
            <Field inline label={t("collection.toggle")}>
              <Switch
                checked={collection.enabled}
                disabled={setCollection.isPending}
                onCheckedChange={toggleCollection}
              />
            </Field>
          </>
        )
      }
    >
      <Field label={t("filters.connection")}>
        {(control) => (
          <Select
            value={connection.id}
            onValueChange={(id) => patch({ connection: id, app: undefined })}
          >
            <SelectTrigger {...control} size="compact" className="min-w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(connections ?? []).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      {periodView && (
        <Field label={t("filters.period")} group>
          <DateRangeInput
            size="compact"
            value={range}
            max={today}
            onValueChange={({ from, to }) =>
              patch({ from: from || undefined, to: to || undefined })
            }
            fromLabel={t("filters.periodFrom")}
            toLabel={t("filters.periodTo")}
          />
        </Field>
      )}
      {view !== "alerts" && (
        <Field label={t("filters.origin")}>
          {(control) => (
            <Select
              value={search.origin ?? ALL}
              onValueChange={(origin) => patch({ origin: origin === ALL ? undefined : origin })}
            >
              <SelectTrigger {...control} size="compact" className="min-w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("filters.allOrigins")}</SelectItem>
                {RELOAD_ORIGINS.map((origin) => (
                  <SelectItem key={origin} value={origin}>
                    {t(`origin.${origin}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      )}
    </FilterBar>
  );

  // The collected views need history; the live queue does not.
  const collected = (content: ReactNode) => {
    if (collectionQuery.isPending) return <Skeleton className="h-64 w-full" />;
    if (collectionQuery.isError) {
      return (
        <FeedbackState
          state="error"
          title={t("error.load")}
          action={
            <Button variant="outline" onClick={() => void collectionQuery.refetch()}>
              <RefreshCw />
              {tc("tryAgain")}
            </Button>
          }
        />
      );
    }
    if (hasHistory) return content;
    if (!collection?.enabled) {
      return (
        <FeedbackState
          state="empty"
          icon={<RefreshCw />}
          title={t("state.off.title")}
          description={t("state.off.description")}
          action={
            <Button onClick={() => toggleCollection(true)} loading={setCollection.isPending}>
              {t("state.off.action")}
            </Button>
          }
        />
      );
    }
    // On, but no history yet: the first collection is running, or it failed —
    // either way the alert above says which.
    return collection.lastError ? null : <Skeleton className="h-64 w-full" />;
  };

  const alertCount = alertsQuery.data?.length ?? 0;
  const viewRuns = (app: string) => patch({ app });
  const runsDescription = [
    t("runs.period", {
      from: formatDate(dayStart(range.from)),
      to: formatDate(dayStart(range.to)),
    }),
    search.origin ? reloadOriginLabel(t, search.origin) : t("filters.allOrigins"),
  ].join(" · ");
  const firstRun = !!collection?.enabled && !collection.lastCollectedAt && !collection.lastError;

  return (
    <PageShell header={header} toolbar={filters}>
      <PageContent>
        {/* Switching connection is search-only navigation and does not remount:
            the key keeps one tenant's table filters out of the next. */}
        <div key={connectionId} className="flex flex-col gap-4">
          {firstRun && (
            <Alert
              tone="info"
              title={t("state.firstRun.title")}
              icon={<Loader2 className="animate-spin" />}
            >
              {t("state.firstRun.description")}
            </Alert>
          )}
          {collection?.enabled && collection.lastError === "forbidden" && (
            <Alert tone="danger" title={t("state.forbidden.title")}>
              {t("state.forbidden.description")}
            </Alert>
          )}
          {collection?.enabled && collection.lastError === "failed" && (
            <Alert tone="warning" title={t("state.failed.title")}>
              {t("state.failed.description")}
            </Alert>
          )}

          <Tabs
            label={t("tabs.label")}
            value={view}
            onValueChange={(next) => patch({ view: next as View })}
            items={[
              {
                value: "now",
                label: t("tabs.now"),
                content: (
                  <NowView
                    connectionId={connectionId}
                    baseUrl={connection.baseUrl}
                    origin={search.origin}
                    onViewRuns={viewRuns}
                  />
                ),
              },
              {
                value: "errors",
                label: t("tabs.errors"),
                content: collected(
                  <ErrorsView
                    connectionId={connectionId}
                    baseUrl={connection.baseUrl}
                    period={period}
                    version={version}
                    onViewRuns={viewRuns}
                  />,
                ),
              },
              {
                value: "bottlenecks",
                label: t("tabs.bottlenecks"),
                content: collected(
                  <BottlenecksView connectionId={connectionId} period={period} version={version} />,
                ),
              },
              {
                value: "performance",
                label: t("tabs.performance"),
                content: collected(
                  <PerformanceView
                    connectionId={connectionId}
                    baseUrl={connection.baseUrl}
                    period={period}
                    version={version}
                    onViewRuns={viewRuns}
                  />,
                ),
              },
              {
                value: "alerts",
                label: (
                  <span className="inline-flex items-center gap-1.5">
                    {t("tabs.alerts")}
                    {alertCount > 0 && (
                      <span className="font-mono text-xs text-status-danger-foreground">
                        {alertCount}
                      </span>
                    )}
                  </span>
                ),
                content: collected(
                  <AlertsView
                    connectionId={connectionId}
                    baseUrl={connection.baseUrl}
                    version={version}
                    onViewRuns={viewRuns}
                  />,
                ),
              },
            ]}
          />
        </div>
        {search.app && (
          <AppRunsDrawer
            key={`${connectionId}|${search.app}`}
            connectionId={connectionId}
            baseUrl={connection.baseUrl}
            appId={search.app}
            period={period}
            version={version}
            description={runsDescription}
            onClose={() => patch({ app: undefined })}
          />
        )}
      </PageContent>
    </PageShell>
  );
}
